/**
 * Firebase Error Log Suppression
 * Suppresses verbose Firebase connection errors while keeping important logs
 * Intercepts Firebase SDK logging at the source
 */

export function setupFirebaseLogSuppression() {
  if (typeof window === 'undefined') return;

  // Store the original console methods
  const originalError = console.error;
  const originalLog = console.log;
  const originalWarn = console.warn;

  // Firebase connection error patterns to suppress
  const firebaseSuppressPatterns = [
    'WebChannelConnection RPC',
    'transport errored',
    'Could not reach Cloud Firestore backend',
    'Connection failed',
    '[code=unavailable]',
    '@firebase/firestore: Firestore',
    'webchannel',
    'defaultLogHandler',
    'This typically indicates',
    'offline mode',
    'healthy Internet connection',
  ];

  // Helper function to check if message should be suppressed
  const shouldSuppressMessage = (messageStr: string): boolean => {
    // Convert to lowercase for case-insensitive comparison
    const lowerMsg = messageStr.toLowerCase();
    
    // Don't suppress non-Firebase logs
    if (!lowerMsg.includes('firebase') && !lowerMsg.includes('webchannel') && !lowerMsg.includes('connection')) {
      return false;
    }
    
    return firebaseSuppressPatterns.some(pattern => 
      lowerMsg.includes(pattern.toLowerCase())
    );
  };

  // Override console.log
  console.log = function(...args: any[]) {
    const messageStr = args.map(arg => 
      typeof arg === 'string' ? arg : JSON.stringify(arg)
    ).join(' ');

    if (!shouldSuppressMessage(messageStr)) {
      originalLog.apply(console, args);
    }
  };

  // Override console.warn
  console.warn = function(...args: any[]) {
    const messageStr = args.map(arg => 
      typeof arg === 'string' ? arg : JSON.stringify(arg)
    ).join(' ');

    if (!shouldSuppressMessage(messageStr)) {
      originalWarn.apply(console, args);
    }
  };

  // Override console.error - most Firebase SDK logs come through here
  console.error = function(...args: any[]) {
    const messageStr = args.map(arg => 
      typeof arg === 'string' ? arg : JSON.stringify(arg)
    ).join(' ');

    if (!shouldSuppressMessage(messageStr)) {
      originalError.apply(console, args);
    }
  };

  // Patch Firebase's internal logging via the logger function
  // Firebase uses a custom logger internally that we need to intercept
  setTimeout(() => {
    try {
      // Look for Firebase logger functions in the global scope
      const globalObj = typeof window !== 'undefined' ? window : global;
      
      // Firebase SDK checks __FIREBASE_DEFAULTS__ and __FIREBASE_INSTANCE__ 
      // We can inject a custom logger
      if ((globalObj as any).__FIREBASE_DEFAULTS__ === undefined) {
        (globalObj as any).__FIREBASE_DEFAULTS__ = {};
      }
      
      // Try to disable debug logging if Firebase has a debug mode
      try {
        const firebaseApp = require('firebase/app');
        if (firebaseApp && firebaseApp.initializeApp) {
          // Firebase is loaded, logging will be intercepted by our console patches
        }
      } catch (e) {
        // Firebase not yet loaded
      }
    } catch (e) {
      // Ignore any errors in patching
    }
  }, 100);

  console.log('🔇 [FIREBASE] Connection error logging suppressed');
}

export default setupFirebaseLogSuppression;
